import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Form,
  Button,
  Card,
  Col,
  Row,
  Spinner,
  Alert,
  InputGroup,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { decryptData } from "../../common/utils";
import AsyncSelect from "react-select/async";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import debounce from "lodash.debounce";

import DatePicker, { registerLocale } from "react-datepicker";
import ptBR from "date-fns/locale/pt-BR";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("ptBR", ptBR);

const FormularioReporte = () => {
  const [programasDisponiveis, setProgramasDisponiveis] = useState([]);
  const [programaSelecionado, setProgramaSelecionado] = useState("");
  const [loadingProgramas, setLoadingProgramas] = useState(true);
  const [selectedAtendimento, setSelectedAtendimento] = useState(null);
  const [formFields, setFormFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [loadingForm, setLoadingForm] = useState(false);
  const [formInfo, setFormInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const dados_outros = decryptData("o");
  const prestadorId = dados_outros?.prestador_id;
  const MySwal = withReactContent(Swal);

  const isDateField = (tipo) => ["date", "data"].includes(tipo?.toLowerCase());

  // Formata a data usando os componentes locais (evita o deslocamento de fuso
  // que ocorre com toISOString, que converte a meia-noite local para UTC).
  const formatLocalDate = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  // Interpreta "YYYY-MM-DD" como data local (new Date("YYYY-MM-DD") assume UTC,
  // o que faz o calendário exibir o dia anterior em fusos negativos como o BRT).
  const parseLocalDate = (value) => {
    if (!value) return null;
    const [y, m, d] = value.split("T")[0].split("-").map(Number);
    return new Date(y, m - 1, d);
  };

  const handleDateChange = (date, fieldName) => {
    const formatted = date ? formatLocalDate(date) : "";
    setFormData((prev) => ({ ...prev, [fieldName]: formatted }));
  };

  useEffect(() => {
    const fetchProgramasDoPrestador = async () => {
      if (!prestadorId) {
        setError("ID do prestador não encontrado.");
        setLoadingProgramas(false);
        return;
      }
      try {
        const response = await api.get(
          `/programas/prestadores/?prestador_id=${prestadorId}`
        );
        const programasComFV = response.data.results.filter(
          (p) => p.dados_programa?.farmacovigilancia_formulario_id
        );
        setProgramasDisponiveis(programasComFV);
      } catch {
        setError("Não foi possível carregar os programas disponíveis.");
      } finally {
        setLoadingProgramas(false);
      }
    };

    fetchProgramasDoPrestador();
  }, [prestadorId]);

  useEffect(() => {
    const fetchFormStructure = async () => {
      if (!programaSelecionado) {
        setFormFields([]);
        setFormData({});
        setSelectedAtendimento(null);
        return;
      }
      setLoadingForm(true);
      setError("");

      try {
        const programa = programasDisponiveis.find(
          (p) => p.programa_id === parseInt(programaSelecionado)
        );
        const formId = programa?.dados_programa.farmacovigilancia_formulario_id;

        if (!formId) {
          setError(
            "O programa selecionado não possui um formulário configurado."
          );
          return;
        }

        const camposResponse = await api.get(
          `/formularios/campos/?formulario_id=${formId}`
        );

        setFormInfo({ id: formId });
        setFormFields(camposResponse.data.results || []);
      } catch {
        setError("Não foi possível carregar os campos do formulário.");
      } finally {
        setLoadingForm(false);
      }
    };

    fetchFormStructure();
  }, [programaSelecionado, programasDisponiveis]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProgramaChange = async (e) => {
    const value = e.target.value;
    if (!value) {
      setProgramaSelecionado("");
      return;
    }

    const programa = programasDisponiveis.find(
      (p) => p.programa_id === parseInt(value)
    );
    const nomePrograma = programa?.dados_programa?.nome || "este programa";

    const { isConfirmed } = await MySwal.fire({
      title: "Confirmar programa?",
      icon: "question",
      html: `<div style="font-size: 0.95rem; line-height: 1.5; color: #444;">
          Você vai realizar o reporte para o programa 
          <strong style="color: #212529;">${nomePrograma}</strong>.
          <br/><br/>
          Em caso de dúvidas, entre em contato pelo e-mail:
          <br/>
          <a 
            href="mailto:suporteaopaciente@conectapku.com.br" 
            style="
              display: inline-block;
              margin-top: 6px;
              padding: 6px 12px;
              background-color: #87b92bff;
              color: #fff;
              text-decoration: none;
              border-radius: 6px;
              font-weight: 500;
              transition: all 0.2s ease-in-out;
            "
            onmouseover="this.style.backgroundColor='#455f14ff'"
            onmouseout="this.style.backgroundColor='#87b92bff'"
          >
            suporteaopaciente@conectapku.com.br
          </a>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Sim, continuar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
      allowOutsideClick: false,
    });

    if (isConfirmed) setProgramaSelecionado(value);
  };

  const performAtendimentoFetch = useCallback(
    async (inputValue, callback) => {
      if (!programaSelecionado) {
        callback([]);
        return;
      }

      try {
        const response = await api.get(
          `/atendimentos/?beneficiario_nome=${inputValue}&programa_id=${programaSelecionado}&cadastro_teste=False&limit=20`
        );

        const opcoes = response.data.results.map((atendimento) => ({
          value: atendimento.id,
          label: `${
            atendimento.dados_beneficiario?.nome || "Paciente sem nome"
          } (ID: ${atendimento.id})`,
          beneficiario_id: atendimento.beneficiario_id,
        }));

        callback(opcoes);
      } catch {
        callback([]);
      }
    },
    [programaSelecionado]
  );

  const debouncedLoadOptions = useMemo(
    () => debounce(performAtendimentoFetch, 500),
    [performAtendimentoFetch]
  );

  const carregarAtendimentos = (inputValue, callback) => {
    if (inputValue.length < 3) {
      callback([]);
      return;
    }
    debouncedLoadOptions(inputValue, callback);
  };

  const handleAtendimentoSelectChange = (selectedOption) => {
    setSelectedAtendimento(selectedOption);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const payload = {
      respostas: formData,
      formulario_id: formInfo.id,
      programa_id: parseInt(programaSelecionado),
      prestador_id: parseInt(prestadorId),
      beneficiario_id: selectedAtendimento?.beneficiario_id || null,
      atendimento_id: selectedAtendimento?.value || null,
    };

    try {
      await api.post("/formularios/respostas/", payload);
      await Swal.fire(
        "Sucesso!",
        "Seu reporte foi enviado com sucesso.",
        "success"
      );
      navigate("/reportes-farmaco");
    } catch (err) {
      const errorData = err.response?.data;
      let errorMessage = "Verifique os dados e tente novamente.";

      if (errorData && typeof errorData === "object") {
        const firstErrorKey = Object.keys(errorData)[0];
        const errorValue = errorData[firstErrorKey];
        errorMessage = Array.isArray(errorValue)
          ? errorValue[0]
          : String(errorValue);
      }

      Swal.fire("Erro no envio", errorMessage, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Row className="align-items-center mb-3">
        <Col>
          <h4 className="page-title">Novo Reporte de Farmacovigilância</h4>
        </Col>
      </Row>

      <Card className="shadow-sm border-0">
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Form onSubmit={handleSubmit}>
            <Row className="mb-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Programa *</Form.Label>
                  {loadingProgramas ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <Form.Select
                      value={programaSelecionado}
                      onChange={handleProgramaChange}
                      required
                    >
                      <option value="">
                        Para qual programa é destinado esse reporte?
                      </option>
                      {programasDisponiveis.map((item) => (
                        <option key={item.programa_id} value={item.programa_id}>
                          {item.dados_programa.nome}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                {programaSelecionado && (
                  <Form.Group>
                    <Form.Label>Paciente (Atendimento) *</Form.Label>
                    <AsyncSelect
                      key={`atendimento_select_${programaSelecionado}`}
                      cacheOptions
                      loadOptions={carregarAtendimentos}
                      defaultOptions
                      name="atendimento_id"
                      placeholder="Digite o nome do paciente (mín. 3 caracteres)..."
                      noOptionsMessage={({ inputValue }) =>
                        inputValue.length < 3
                          ? "Digite ao menos 3 caracteres"
                          : "Nenhum paciente/atendimento encontrado"
                      }
                      onChange={handleAtendimentoSelectChange}
                      value={selectedAtendimento}
                      required
                    />
                  </Form.Group>
                )}
              </Col>
            </Row>

            <hr className="my-4" />

            {loadingForm && (
              <div className="text-center p-3">
                <Spinner animation="border" />
              </div>
            )}

            <Row>
              {!loadingForm &&
                formFields.map((field) => {
                  if (["paciente_nome_iniciais"].includes(field.slug))
                    return null;

                  if (isDateField(field.tipo)) {
                    return (
                      <Col md={6} key={field.slug} className="mb-3">
                        <Form.Group>
                          <Form.Label>
                            {field.nome}
                            {field.obrigatorio && " *"}
                          </Form.Label>
                          <InputGroup>
                            <DatePicker
                              selected={parseLocalDate(formData[field.slug])}
                              onChange={(date) =>
                                handleDateChange(date, field.slug)
                              }
                              dateFormat="dd/MMM/yyyy"
                              locale="ptBR"
                              placeholderText="Selecione a data"
                              className="form-control"
                              calendarClassName="shadow-sm border"
                              required={field.obrigatorio}
                            />
                            <InputGroup.Text className="bg-light border-0">
                              <i className="bi bi-calendar-event text-secondary"></i>
                            </InputGroup.Text>
                          </InputGroup>
                        </Form.Group>
                      </Col>
                    );
                  }

                  if (field.tipo === "textarea") {
                    return (
                      <Col md={12} key={field.slug} className="mb-3">
                        <Form.Group>
                          <Form.Label>
                            {field.nome}
                            {field.obrigatorio && " *"}
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={5}
                            name={field.slug}
                            required={field.obrigatorio}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </Col>
                    );
                  }

                  return (
                    <Col md={6} key={field.slug} className="mb-3">
                      <Form.Group>
                        <Form.Label>
                          {field.nome}
                          {field.obrigatorio && " *"}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name={field.slug}
                          required={field.obrigatorio}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </Col>
                  );
                })}
            </Row>

            <div className="d-flex justify-content-end mt-4">
              <Button
                variant="light"
                size="sm"
                onClick={() => navigate("/reportes-farmaco")}
                className="me-2 border"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="success"
                size="sm"
                disabled={
                  submitting ||
                  !programaSelecionado ||
                  !selectedAtendimento ||
                  loadingForm
                }
              >
                {submitting ? (
                  <Spinner as="span" animation="border" size="sm" />
                ) : (
                  "Enviar Reporte"
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default FormularioReporte;
